/**
 * AdminFeatureAccessMatrixPage - Pass 2
 * /admin/feature-access
 *
 * Toggles features at four scopes (most-specific wins):
 *   global → category → pilot_subset → individual_host
 *
 * Per Shelsea Pilot Handoff: admin must explicitly toggle features per
 * host dashboard, per pilot category, or globally.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const SCOPE_OPTIONS = [
  { value: "global", label: "Global (all hosts)" },
  { value: "category", label: "Category (all hosts in a category)" },
  { value: "pilot_subset", label: "Pilot subset (named pilot id)" },
  { value: "individual_host", label: "Individual host (user_id)" },
];

const SCOPE_COLORS = {
  global: "bg-blue-50 text-blue-700 border-blue-200",
  category: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pilot_subset: "bg-amber-50 text-amber-700 border-amber-200",
  individual_host: "bg-violet-50 text-violet-700 border-violet-200",
};

export default function AdminFeatureAccessMatrixPage() {
  const navigate = useNavigate();
  const [flags, setFlags] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState({ feature_key: "", scope: "global", scope_value: "", enabled: true, notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/feature-access-matrix`, { headers: auth() });
      if (!r.ok) throw new Error("Failed to load feature flags");
      const d = await r.json();
      setFlags(d.flags || []);
      setGrouped(d.grouped_by_feature || {});
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upsert = async (flag) => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/admin/feature-access-matrix`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify(flag),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success("Feature flag saved");
      setShowAdd(false);
      setDraft({ feature_key: "", scope: "global", scope_value: "", enabled: true, notes: "" });
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleFlag = async (f) => {
    await upsert({ ...f, enabled: !f.enabled });
  };

  const removeFlag = async (f) => {
    if (!confirm(`Remove flag "${f.feature_key}" (${f.scope})?`)) return;
    try {
      const params = new URLSearchParams({
        feature_key: f.feature_key,
        scope: f.scope,
        ...(f.scope_value ? { scope_value: f.scope_value } : {}),
      });
      const r = await fetch(`${API}/admin/feature-access-matrix?${params}`, {
        method: "DELETE",
        headers: auth(),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success("Flag removed");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const filteredKeys = useMemo(() => {
    return Object.keys(grouped)
      .filter(k => !filter || k.toLowerCase().includes(filter.toLowerCase()))
      .sort();
  }, [grouped, filter]);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-feature-access-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/hub")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin Hub
            </Button>
            <h1 className="text-2xl font-light tracking-tight">Pilot Feature Access Matrix</h1>
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-stone-100 text-stone-700 border border-stone-200">
              {flags.length} flags
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-flag-btn">
              <Plus className="w-4 h-4 mr-1" /> Add flag
            </Button>
          </div>
        </div>

        <p className="text-sm text-stone-600 mb-4">
          Resolution order: <strong>individual_host</strong> → <strong>pilot_subset</strong> → <strong>category</strong> → <strong>global</strong>.
          Most-specific scope wins.
        </p>

        <Input
          placeholder="Filter by feature key..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="mb-4 max-w-md"
          data-testid="filter-input"
        />

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
          </div>
        ) : filteredKeys.length === 0 ? (
          <div className="text-center py-16 text-stone-500" data-testid="empty-state">
            No feature flags yet. Click "Add flag" to create your first.
          </div>
        ) : (
          <div className="space-y-4" data-testid="flag-groups">
            {filteredKeys.map((featureKey) => (
              <div key={featureKey} className="bg-white border border-stone-200 rounded-lg p-4" data-testid={`flag-group-${featureKey}`}>
                <div className="font-mono text-sm font-medium mb-3">{featureKey}</div>
                <div className="space-y-2">
                  {grouped[featureKey].map((f, idx) => (
                    <div
                      key={`${f.scope}-${f.scope_value || "all"}-${idx}`}
                      className="flex items-center gap-3 px-3 py-2 rounded-md bg-stone-50 border border-stone-100"
                      data-testid={`flag-row-${f.scope}`}
                    >
                      <span className={`px-2 py-0.5 text-xs rounded-full border ${SCOPE_COLORS[f.scope] || ""}`}>
                        {f.scope}
                      </span>
                      {f.scope_value && (
                        <span className="text-xs font-mono text-stone-600">{f.scope_value}</span>
                      )}
                      {f.notes && <span className="text-xs text-stone-500 italic flex-1 truncate">{f.notes}</span>}
                      <div className="ml-auto flex items-center gap-2">
                        <Switch
                          checked={f.enabled}
                          onCheckedChange={() => toggleFlag(f)}
                          data-testid={`toggle-${f.scope}`}
                        />
                        <Button variant="ghost" size="sm" onClick={() => removeFlag(f)} data-testid={`remove-${f.scope}`}>
                          <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {showAdd && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="add-flag-modal">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h2 className="text-lg font-medium mb-4">Add a feature flag</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-stone-600 mb-1 block">Feature key</label>
                  <Input
                    placeholder="e.g. acuity_auto_link, instructor_dashboard"
                    value={draft.feature_key}
                    onChange={(e) => setDraft({ ...draft, feature_key: e.target.value })}
                    data-testid="feature-key-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-stone-600 mb-1 block">Scope</label>
                  <select
                    value={draft.scope}
                    onChange={(e) => setDraft({ ...draft, scope: e.target.value, scope_value: "" })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm"
                    data-testid="scope-select"
                  >
                    {SCOPE_OPTIONS.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
                  </select>
                </div>
                {draft.scope !== "global" && (
                  <div>
                    <label className="text-xs text-stone-600 mb-1 block">
                      Scope value ({draft.scope === "category" ? "category_id" : draft.scope === "individual_host" ? "user_id" : "pilot_id"})
                    </label>
                    <Input
                      placeholder={draft.scope === "category" ? "e.g. makeup" : draft.scope === "individual_host" ? "e.g. usr_shelsea_patton" : "e.g. shelsea_pilot"}
                      value={draft.scope_value}
                      onChange={(e) => setDraft({ ...draft, scope_value: e.target.value })}
                      data-testid="scope-value-input"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Switch
                    checked={draft.enabled}
                    onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
                    data-testid="enabled-switch"
                  />
                  <span className="text-sm">Enabled</span>
                </div>
                <Input
                  placeholder="Notes (optional)"
                  value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  data-testid="notes-input"
                />
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <Button variant="ghost" onClick={() => setShowAdd(false)} data-testid="cancel-add">Cancel</Button>
                <Button
                  onClick={() => upsert(draft)}
                  disabled={submitting || !draft.feature_key || (draft.scope !== "global" && !draft.scope_value)}
                  data-testid="save-flag"
                >
                  {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save flag
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
